import { FOCUS_RING_NO_RADIUS } from '@amelio/ui-library';
import { useRef } from 'react';

import type { TranslationKey } from '@/i18n-key';
import type { CycleEvaluationDashboardTab } from '@/types/cycle-evaluation/CycleEvaluationDashboardTab';
import type { KeyboardEvent, Ref } from 'react';

import { useAmelioTranslations } from '@/hooks';
import { useArrowNavigation } from '@/hooks/utils/useArrowNavigation';

const TABS: CycleEvaluationDashboardTab[] = [
    'evaluationPeriods',
    'progressTracking',
    'objectivePeriods',
];

// The tab owns its radius, so the shared ring must not bring its own.
const TAB_BASE_CLASSES = `rounded-lg font-medium transition-colors max-md:px-[16px] max-md:py-[8px] max-md:text-[12px] max-md:leading-[16px] md:px-[24px] md:py-[12px] md:text-[14px] md:leading-[20px] ${FOCUS_RING_NO_RADIUS}`;

const TAB_SELECTED_CLASSES = 'bg-white text-base shadow-md';

const TAB_IDLE_CLASSES = 'text-base-400 hover:text-base-600 bg-white/40 hover:bg-white/60';

const getCycleEvaluationTabLabelKey = (tab: CycleEvaluationDashboardTab): TranslationKey =>
    `cycleEvaluationDashboard.tabs.${tab}`;

const isCycleEvaluationDashboardTab = (value: unknown): value is CycleEvaluationDashboardTab =>
    typeof value === 'string' && TABS.includes(value as CycleEvaluationDashboardTab);

interface DashboardTabAccess {
    /** The all-departments scope: evaluation periods and progress tracking. */
    canManageEvaluationCycles: boolean;
    /** The objectives module's own permission: the objective periods. */
    canManageObjectivePeriods: boolean;
}

/**
 * The tabs a reader may open, in the fixed order. The dashboard serves two audiences -- cycle
 * administrators and objectives managers -- and each only sees the tabs their scope covers.
 */
const getAvailableDashboardTabs = ({
    canManageEvaluationCycles,
    canManageObjectivePeriods,
}: DashboardTabAccess): CycleEvaluationDashboardTab[] =>
    TABS.filter((tab) =>
        tab === 'objectivePeriods' ? canManageObjectivePeriods : canManageEvaluationCycles,
    );

interface CycleEvaluationTabButtonProps {
    tab: CycleEvaluationDashboardTab;
    isSelected: boolean;
    onSelect: (tab: CycleEvaluationDashboardTab) => void;
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
    ref?: Ref<HTMLButtonElement>;
}

const CycleEvaluationTabButton = ({
    tab,
    isSelected,
    onSelect,
    onKeyDown,
    ref,
}: CycleEvaluationTabButtonProps) => {
    const { t } = useAmelioTranslations();

    return (
        <button
            ref={ref}
            type={'button'}
            role={'tab'}
            aria-selected={isSelected}
            tabIndex={isSelected ? 0 : -1}
            onClick={() => onSelect(tab)}
            onKeyDown={onKeyDown}
            className={`${TAB_BASE_CLASSES} ${isSelected ? TAB_SELECTED_CLASSES : TAB_IDLE_CLASSES}`}
            data-testid={`cycle-evaluation-tab-${tab}`}
        >
            {t(getCycleEvaluationTabLabelKey(tab))}
        </button>
    );
};

interface CycleEvaluationTabsProps {
    activeTab: CycleEvaluationDashboardTab;
    onTabChange: (tab: CycleEvaluationDashboardTab) => void;
    /** The tabs to offer, in the fixed order; every tab when omitted. */
    tabs?: CycleEvaluationDashboardTab[];
    className?: string;
}

const CycleEvaluationTabs = ({
    activeTab,
    onTabChange,
    tabs = TABS,
    className,
}: CycleEvaluationTabsProps) => {
    const { t } = useAmelioTranslations();
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Automatic activation: an arrow both focuses and opens the tab it lands on, so the row holds
    // a single tab stop -- the open tab -- and the panel follows the keyboard.
    const handleKeyDown = useArrowNavigation({
        itemCount: tabs.length,
        refs: tabRefs,
        onNavigate: (index) => {
            if (tabs[index] !== activeTab) {
                onTabChange(tabs[index]);
            }
        },
    });

    return (
        <div
            className={className}
            role={'tablist'}
            aria-label={t('cycleEvaluationDashboard.tabsAriaLabel')}
            data-testid={'cycle-evaluation-tabs'}
        >
            <div className={'flex flex-wrap gap-[8px]'}>
                {tabs.map((tab, index) => (
                    <CycleEvaluationTabButton
                        key={tab}
                        tab={tab}
                        isSelected={activeTab === tab}
                        onSelect={onTabChange}
                        onKeyDown={(event) => handleKeyDown(event, index)}
                        ref={(element) => {
                            tabRefs.current[index] = element;
                        }}
                    />
                ))}
            </div>
        </div>
    );
};

export {
    CycleEvaluationTabs,
    getAvailableDashboardTabs,
    getCycleEvaluationTabLabelKey,
    isCycleEvaluationDashboardTab,
};
